import { useEffect, useRef, useState } from "react";
import type { PlayProps } from "../types";
import { publicTableModel } from "../public-presentation";
/** "It's your turn" for the authenticated seat, built only from public fields
 * (DISCLOSURE "Shared components"): the seat is in `requiredPlayerIds` or holds
 * the phase's public actor field (turn, office, guide, answerer, accused). Never
 * reads `legalActions` or `private`, never fires for a display or spectator, and
 * never at Mafia night (the engine publishes `requiredPlayerIds: []` there and
 * names no actor; the explicit guard keeps that true by construction). */
type TurnInput = {
  state: Pick<
    PlayProps["state"],
    | "roomInstanceId"
    | "playerId"
    | "matchId"
    | "phaseId"
    | "isDisplay"
    | "isSpectator"
    | "status"
  > & {
    game: Pick<PlayProps["state"]["game"], "id" | "phase" | "finished" | "public">;
  };
  isDisplay: boolean;
};
export function isPublicTurn({ state, isDisplay }: TurnInput): boolean {
  const g = state.game,
    me = state.playerId;
  if (!me || isDisplay || state.isDisplay || state.isSpectator) return false;
  if (!g || g.finished || state.status === "finished") return false;
  if (g.id === "mafia" && g.phase === "night") return false;
  const required: unknown = g.public?.requiredPlayerIds;
  if (Array.isArray(required) && required.includes(me)) return true;
  return publicTableModel(g, []).actorId === me;
}
/** Identity of one "your turn" occurrence: a new phase or a new seat is a new turn. */
export function turnSignalKey(input: TurnInput): string | null {
  if (!isPublicTurn(input)) return null;
  const s = input.state;
  return JSON.stringify([
    s.roomInstanceId ?? "",
    s.playerId,
    s.matchId,
    s.phaseId ?? s.game.public?.phaseKey ?? null,
  ]);
}
export const TURN_TITLE_PREFIX = "● Your turn · ";
export function withTurnTitle(title: string, on: boolean) {
  const base = title.startsWith(TURN_TITLE_PREFIX)
    ? title.slice(TURN_TITLE_PREFIX.length)
    : title;
  return on ? TURN_TITLE_PREFIX + base : base;
}
// One buzz and one announcement per turn per page, even if the layout remounts.
const signalled = new Set<string>();
export function resetTurnSignals() {
  signalled.clear();
}
/** Side effects for the turn signal plus a polite announcement string for one
 * live region: "Your turn: <phase>" when this seat becomes the public actor,
 * otherwise the public phase title when it changes. */
export function useTurnSignal({
  state,
  isDisplay,
  phaseTitle,
  publicTitle,
}: TurnInput & { phaseTitle: string; publicTitle: string }): string {
  const key = turnSignalKey({ state, isDisplay });
  const [announcement, setAnnouncement] = useState("");
  const lastTitle = useRef<string | null>(null);
  useEffect(() => {
    if (!key || typeof document === "undefined") return;
    document.title = withTurnTitle(document.title, true);
    if (!signalled.has(key)) {
      signalled.add(key);
      if (signalled.size > 500)
        signalled.delete(signalled.values().next().value as string);
      try {
        navigator.vibrate?.(60);
      } catch {
        /* Unsupported or blocked: the title and announcement still carry it. */
      }
      setAnnouncement(`Your turn: ${phaseTitle}`);
    }
    return () => {
      document.title = withTurnTitle(document.title, false);
    };
    // The phase title belongs to this turn; only a new turn re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    const previous = lastTitle.current;
    lastTitle.current = publicTitle;
    if (previous === null || previous === publicTitle || key) return;
    setAnnouncement(publicTitle);
  }, [publicTitle, key]);
  return announcement;
}
